/**
 * Policy Acknowledgment Service
 *
 * Covers two concerns:
 *   1. Staff-side: create acknowledgment campaigns via authenticated API (JWT)
 *   2. Driver-side: fetch + sign via public token-gated endpoints (no auth)
 */
import axios from 'axios'
import { api } from './api'

// --- Staff-side (uses auth-intercepted `api` instance) ---

export interface CreateCampaignResponse {
  campaign_id: string
  /**
   * The roster size the campaign aimed at — NOT how many drivers were reached.
   * Reporting this number alone is what let a 19-driver send that reached 10
   * read as complete (2026-08-10). Render `links_sent` beside it, always.
   */
  drivers_targeted: number
  /** Drivers whose magic-link email was CONFIRMED sent (durable link_sent_at). */
  links_sent: number
  /** Send attempted and failed — retryable. */
  links_failed: number
  /** No address on file — a data gap, not a send failure. Different fix. */
  drivers_without_email: number
  sent_at: string
}

// --- Driver-side types (public, no auth) ---

export interface PolicyAcknowledgmentTokenData {
  policy_type: string
  policy_label: string
  /**
   * WorkDrive file id of the source document — NOT a browser-loadable URL.
   * A `policy_pdf_url` field declared here from 2026-05-08 to 2026-09-01 was
   * never returned by the API, so the sign page's iframe rendered with no src
   * and drivers signed a document they were never shown. The PDF is loaded
   * via pdfUrlForToken() instead; this id only signals whether a document
   * exists to load.
   */
  policy_pdf_workdrive_id: string | null
  client_legal_name: string
  driver_name: string
  signed_at: string | null
}

export interface PolicySignResponse {
  signed_at: string
}

// Public axios — no auth interceptor, just the base URL.
const PUBLIC_API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://api.claimshieldpro.com/api'

const publicAxios = axios.create({ baseURL: PUBLIC_API_BASE })

export const policyAcknowledgmentService = {
  /**
   * Create an acknowledgment campaign targeting all active drivers of a client.
   * Staff/admin only — uses the JWT-authenticated `api` instance.
   */
  async createCampaign(
    clientId: string,
    documentId: string,
    audience: 'active_drivers' = 'active_drivers',
  ): Promise<CreateCampaignResponse> {
    const { data } = await api.post<CreateCampaignResponse>(
      `/v1/clients/${clientId}/documents/${documentId}/send-acknowledgments`,
      { audience },
    )
    return data
  },

  /**
   * Fetch policy metadata for a driver via their magic-link token.
   * Public — no JWT required.
   */
  async fetchByToken(token: string): Promise<PolicyAcknowledgmentTokenData> {
    const { data } = await publicAxios.get<PolicyAcknowledgmentTokenData>(
      `/v1/policy-acknowledgments/${token}`,
    )
    return data
  },

  /**
   * Absolute URL that streams the policy PDF for this token — used for the
   * "open in a new tab" link. Served by Sentinel
   * (GET /v1/policy-acknowledgments/{token}/pdf), which proxies WorkDrive.
   *
   * Do NOT put this URL in an iframe: the portal's CSP frame-src does not
   * allow framing the api origin (verified live 2026-09-01 — the frame is
   * blocked silently). Inline display goes through fetchPdfBlob() and a
   * blob: URL, which frame-src does allow.
   */
  pdfUrlForToken(token: string): string {
    return `${PUBLIC_API_BASE}/v1/policy-acknowledgments/${encodeURIComponent(token)}/pdf`
  },

  /**
   * Fetch the policy PDF bytes for inline display. The caller turns this
   * into a blob: object URL for the iframe — the only frame-src-permitted
   * way to render the document inside the portal page.
   */
  async fetchPdfBlob(token: string): Promise<Blob> {
    const { data } = await publicAxios.get<Blob>(
      `/v1/policy-acknowledgments/${encodeURIComponent(token)}/pdf`,
      { responseType: 'blob' },
    )
    return data
  },

  /**
   * Submit a driver's signature for a policy acknowledgment.
   * Public — no JWT required.
   */
  async sign(
    token: string,
    signaturePngBase64: string,
    typedName: string,
  ): Promise<PolicySignResponse> {
    const { data } = await publicAxios.post<PolicySignResponse>(
      `/v1/policy-acknowledgments/${token}/sign`,
      {
        signature_png_base64: signaturePngBase64,
        typed_name: typedName,
      },
    )
    return data
  },
}
